import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { StagingEventTypesPanel } from "@/features/supply-config/ui/calendly-panels";
import {
  StagingSyncStatus,
  getStagingSyncStatus,
  startStagingSync,
} from "@/features/supply-config/calendly-api";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

const PHASE_LABEL: Record<string, string> = {
  fetching: "Fetching from Calendly",
  saving: "Saving",
  cleaning: "Cleaning up",
  done: "Done",
};

export default function EventTypeStaging() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Polls while a sync is running; re-attaches automatically on mount.
  const { data: status } = useQuery({
    queryKey: ["staging-sync-status"],
    queryFn: getStagingSyncStatus,
    refetchInterval: (q) =>
      q.state.data?.status === "RUNNING" ? 2500 : false,
    staleTime: 0,
  });
  const running = status?.status === "RUNNING";

  const start = useMutation({
    mutationFn: startStagingSync,
    onSuccess: (s) => qc.setQueryData(["staging-sync-status"], s),
    onError: (e: any) =>
      toast({
        title: "Couldn't start",
        description: e.response?.data?.message || "Failed to start sync",
        variant: "destructive",
      }),
  });

  // When a run transitions out of RUNNING, refresh the table + notify.
  const prev = useRef<StagingSyncStatus["status"] | undefined>(undefined);
  useEffect(() => {
    const s = status?.status;
    if (prev.current === "RUNNING" && s === "COMPLETED") {
      qc.invalidateQueries({ queryKey: ["staging-event-types"] });
      qc.invalidateQueries({ queryKey: ["calendly-derived-managed-events"] });
      toast({
        title: "Raw data updated",
        description: `${status?.fetched ?? 0} fetched · ${status?.created ?? 0} created · ${status?.updated ?? 0} updated · ${status?.removed ?? 0} removed`,
      });
    }
    if (prev.current === "RUNNING" && s === "FAILED") {
      toast({
        title: "Sync failed",
        description: status?.error || "Failed to sync from Calendly",
        variant: "destructive",
      });
    }
    prev.current = s;
  }, [status?.status]);

  return (
    <div className="py-6 space-y-4">
      <div className="px-6 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("..")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">
            Event Types — Raw Calendly data
          </h1>
        </div>
        <HasAccess permission="write">
          <Button
            className="bg-sidebar"
            disabled={running || start.isPending}
            onClick={() => start.mutate()}
          >
            <RefreshCw
              className={`h-4 w-4 mr-1 ${running || start.isPending ? "animate-spin" : ""}`}
            />
            {running ? "Updating…" : "Update from Calendly"}
          </Button>
        </HasAccess>
      </div>

      {status && status.status !== "IDLE" && (
        <div className="px-6">
          <div className="flex items-center gap-3 rounded-md border border-gray-200 p-3 text-sm">
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin text-blue-500 shrink-0" />
            ) : status.status === "COMPLETED" ? (
              <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 text-red-500 shrink-0" />
            )}
            <div className="min-w-0">
              <p className="font-medium">
                {running
                  ? `Updating… (${PHASE_LABEL[status.phase ?? "fetching"]})`
                  : status.status === "COMPLETED"
                    ? "Up to date"
                    : "Sync failed"}
              </p>
              <p className="text-xs text-gray-500">
                {status.fetched ?? 0} fetched · {status.created ?? 0} created ·{" "}
                {status.updated ?? 0} updated · {status.removed ?? 0} removed
                {status.error ? ` · ${status.error}` : ""}
              </p>
            </div>
          </div>
        </div>
      )}

      <StagingEventTypesPanel />
    </div>
  );
}
